import React, { useState, useEffect } from 'react'
import { CheckCircle, XCircle, CircleNotch, Plugs } from '@phosphor-icons/react'
import { getErrorMessage } from '@/lib/api'

interface TestConnectionButtonProps {
  onTest: () => Promise<any>
  label?: string
}

type TestState = 'idle' | 'testing' | 'success' | 'error'

const TestConnectionButton: React.FC<TestConnectionButtonProps> = ({ onTest, label = 'Test Connection' }) => {
  const [state, setState] = useState<TestState>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    if (state === 'success' || state === 'error') {
      const timer = setTimeout(() => setState('idle'), 4000)
      return () => clearTimeout(timer)
    }
  }, [state])

  const handleTest = async () => {
    setState('testing')
    setErrorMsg('')
    try {
      await onTest()
      setState('success')
    } catch (e) {
      // getErrorMessage flattens a raw FastAPI 422 array (which would otherwise
      // crash this render trying to display an object) into plain text, and
      // falls back to a friendly default instead of a bare axios message.
      setErrorMsg(getErrorMessage(e, 'Connection failed'))
      setState('error')
    }
  }

  const config: Record<TestState, { className: string; icon: React.ReactNode; text: string }> = {
    idle: {
      className: 'border-border/60 dark:border-white/[0.08] text-foreground/80 dark:text-[#e2e4e7] hover:border-[#5e6ad2]/40 hover:text-[#5e6ad2] hover:bg-[#5e6ad2]/[0.08] dark:hover:border-[#5e6ad2]/50 dark:hover:text-[#a5b4fc] dark:hover:bg-[#5e6ad2]/[0.12]',
      icon: <Plugs className="w-4 h-4" />,
      text: label,
    },
    testing: {
      className: 'border-[#5e6ad2]/40 text-[#5e6ad2] dark:border-[#5e6ad2]/50 dark:text-[#a5b4fc] cursor-wait',
      icon: <CircleNotch className="w-4 h-4 animate-spin" />,
      text: 'Testing...',
    },
    success: {
      className: 'border-emerald-500/30 bg-emerald-500/[0.08] text-emerald-700 dark:border-emerald-500/35 dark:bg-emerald-500/[0.12] dark:text-emerald-300',
      icon: <CheckCircle className="w-4 h-4" weight="fill" />,
      text: 'Connected!',
    },
    error: {
      className: 'border-red-500/30 bg-red-500/[0.08] text-red-700 dark:border-red-500/35 dark:bg-red-500/[0.12] dark:text-red-300',
      icon: <XCircle className="w-4 h-4" weight="fill" />,
      text: errorMsg || 'Failed',
    },
  }

  const c = config[state]

  return (
    <button
      type="button"
      onClick={handleTest}
      disabled={state === 'testing'}
      className={`inline-flex items-center gap-2 h-9 px-4 border rounded-md transition-colors duration-150 ${c.className}`}
      style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
    >
      {c.icon}
      <span className="truncate max-w-[200px]">{c.text}</span>
    </button>
  )
}

export default TestConnectionButton
